import type { PublicSportsFacilityDTO, SportsClubDTO, SportsFacilityDTO } from '@ui/shared/util-api';
import { MapItem } from '@ui/shared/util-map';
import { TypeFilterOptions } from './filter-option-keys';
import type { LinkedMapItem } from './linked-map-item';

type OneOfSportMapItems
    = { sportsClub: SportsClubDTO; sportsFacility?: never; publicSportsFacility?: never }
    | { sportsClub?: never; sportsFacility: SportsFacilityDTO; publicSportsFacility?: never }
    | { sportsClub?: never; sportsFacility?: never; publicSportsFacility: PublicSportsFacilityDTO };

type MapItemWithLinkedItems = OneOfSportMapItems & { linkedItems: Array<LinkedMapItem> };

const typeValues: Record<TypeFilterOptions, { typeLabel: string; linkedItemsLabel: string }> = {
    [TypeFilterOptions.SportClub]: { typeLabel: 'Sportverein', linkedItemsLabel: 'Sportstätten' },
    [TypeFilterOptions.SportFacility]: { typeLabel: 'Sportstätte', linkedItemsLabel: 'Sportvereine' },
    [TypeFilterOptions.PublicSportFacility]: { typeLabel: 'Öffentliche Sportanlage', linkedItemsLabel: '' },
    [TypeFilterOptions.All]: { typeLabel: '', linkedItemsLabel: '' },
};

export class SportMapItem extends MapItem {
    public readonly imagePath: string;
    public readonly thumbnailPath: string;
    public readonly type: TypeFilterOptions;
    public readonly typeLabel: string;
    public readonly accessibilityFeatures: Array<string>;
    public readonly openingTimes: string;
    public readonly contact: string;
    public readonly weblink: string;
    public readonly linkedItemsLabel: string;
    public readonly linkedItems: Array<LinkedMapItem>;
    public readonly locationDescription: string;

    constructor({ sportsClub, sportsFacility, publicSportsFacility, linkedItems }: MapItemWithLinkedItems) {
        const mapItem = sportsClub ?? sportsFacility ?? publicSportsFacility;
        const nonPublicMapItem = sportsClub ?? sportsFacility;
        const isSportsClub = !!sportsClub;
        const isSportsFacility = !!sportsFacility;
        const isPublicSportsFacility = !!publicSportsFacility;

        const type
            = isSportsClub
                ? TypeFilterOptions.SportClub
                : isSportsFacility
                    ? TypeFilterOptions.SportFacility
                    : TypeFilterOptions.PublicSportFacility;

        super(
            mapItem,
            `id-${ mapItem.id }-type-${ type }`,
        );

        this.type = type;
        this.iconPath = mapItem.icon ?? '';
        this.imagePath = mapItem.image?.thumbs?.detail ?? mapItem.image?.image ?? '';
        this.thumbnailPath = mapItem.image?.thumbs?.preview ?? this.imagePath;
        this.typeLabel = typeValues[type].typeLabel;
        this.iconLabel = this.typeLabel ? `Typ: ${ this.typeLabel }` : '';
        this.accessibilityFeatures = mapItem.accessibility_features.map(feature => feature.name);
        this.openingTimes
            = nonPublicMapItem?.opening_hours
                ? `<pre>${ nonPublicMapItem.opening_hours }</pre>`
                : '';
        this.contact = nonPublicMapItem?.contact_person ?? '';
        this.weblink = nonPublicMapItem?.website ?? '';
        this.linkedItemsLabel = typeValues[type].linkedItemsLabel;
        this.locationDescription
            = isPublicSportsFacility
                ? publicSportsFacility.location_description
                : '';
        this.linkedItems = linkedItems;
    }
}
